import HttpFactory from "~/repository/factory";
import type {
  StudentOnboarding,
  StudentRequest,
  StudentRequestItemInput,
  StudentRequestKind,
  StudentRequestsPage,
  StudentRequestStatus,
  StudentRequestTemplate,
  StudentRequestTemplateInput,
  StudentRequestFile,
  SchoolStudentEntryQueueItem,
  SchoolEntryQueueFilters,
  ApprovedStudentResponses,
} from "~/types/studentRequests";

const base = "/api/student-requests/";
const templates = `${base}templates/`;
const requests = `${base}requests/`;
type RequestScope = "self";

function scopedUrl(url: string, scope?: RequestScope): string {
  return scope ? `${url}?scope=${scope}` : url;
}

function results<T>(value: T[] | StudentRequestsPage<T>): T[] {
  return Array.isArray(value) ? value : value.results;
}

export default class StudentRequestsModule extends HttpFactory {
  async listTemplates() {
    return results(
      await this.get<
        StudentRequestTemplate[] | StudentRequestsPage<StudentRequestTemplate>
      >(templates),
    );
  }

  getTemplate(id: string) {
    return this.get<StudentRequestTemplate>(`${templates}${id}/`);
  }

  createTemplate(input: StudentRequestTemplateInput) {
    return this.post<StudentRequestTemplate>(templates, input);
  }

  updateTemplate(id: string, input: StudentRequestTemplateInput) {
    return this.patch<StudentRequestTemplate>(`${templates}${id}/`, input);
  }

  deleteTemplate(id: string) {
    return this.delete<void>(`${templates}${id}/`);
  }

  applyTemplate(id: string, studentIds: number[], onboarding = false) {
    return this.post<unknown>(`${templates}${id}/apply/`, {
      student_ids: studentIds,
      ...(onboarding ? { onboarding: true } : {}),
    });
  }

  async listRequests(
    filters: {
      student_id?: number;
      status?: StudentRequestStatus;
      kind?: StudentRequestKind;
      scope?: RequestScope;
    } = {},
  ) {
    return results(
      await this.get<StudentRequest[] | StudentRequestsPage<StudentRequest>>(
        requests,
        {
          student_id: filters.student_id,
          status: filters.status,
          kind: filters.kind,
          scope: filters.scope,
        },
      ),
    );
  }

  createRequests(input: {
    student_ids: number[];
    items: StudentRequestItemInput[];
    onboarding?: boolean;
  }) {
    return this.post<unknown>(requests, input);
  }

  getRequest(id: string, scope?: RequestScope) {
    return this.get<StudentRequest>(scopedUrl(`${requests}${id}/`, scope));
  }

  saveDraft(
    id: string,
    value: string | Record<string, string>,
    scope?: RequestScope,
  ) {
    return this.patch<StudentRequest>(
      scopedUrl(`${requests}${id}/draft/`, scope),
      { value },
    );
  }

  submit(
    id: string,
    value?: string | Record<string, string>,
    scope?: RequestScope,
  ) {
    return this.post<StudentRequest>(
      scopedUrl(`${requests}${id}/submit/`, scope),
      value === undefined ? {} : { value },
    );
  }

  uploadFile(id: string, file: File, scope?: RequestScope) {
    const body = new FormData();
    body.append("file", file);
    return this.post<StudentRequestFile>(
      scopedUrl(`${requests}${id}/files/`, scope),
      body,
    );
  }

  deleteFile(requestId: string, fileId: string, scope?: RequestScope) {
    return this.delete<void>(
      scopedUrl(`${requests}${requestId}/files/${fileId}/`, scope),
    );
  }

  downloadFile(requestId: string, fileId: string, scope?: RequestScope) {
    return this.get<Blob>(
      scopedUrl(`${requests}${requestId}/files/${fileId}/download/`, scope),
      {},
      { responseType: "blob" },
    );
  }

  review(
    id: string,
    action: "approve" | "request_changes" | "cancel",
    note = "",
  ) {
    return this.post<StudentRequest>(`${requests}${id}/review/`, {
      action,
      note,
    });
  }

  getOnboarding(studentId: number) {
    return this.get<StudentOnboarding>(`${base}onboarding/${studentId}/`);
  }

  getMyOnboarding() {
    return this.get<StudentOnboarding | null>(`${base}my-onboarding/`);
  }

  configureOnboarding(studentId: number, templateId?: string) {
    return this.post<StudentOnboarding>(
      `${base}onboarding/${studentId}/configure/`,
      templateId ? { template_id: templateId } : {},
    );
  }

  listSchoolQueue(filters: SchoolEntryQueueFilters = {}) {
    return this.get<StudentRequestsPage<SchoolStudentEntryQueueItem>>(
      `${base}school-queue/`,
      { ...filters },
    );
  }

  getApprovedResponses(studentId: number) {
    return this.get<ApprovedStudentResponses>(
      `${base}onboarding/${studentId}/approved-responses/`,
    );
  }

  downloadApprovedFile(studentId: number, fileId: string) {
    return this.get<Blob>(
      `${base}onboarding/${studentId}/approved-files/${fileId}/download/`,
      {},
      { responseType: "blob" },
    );
  }
}
